import { useState } from 'react';
import {
  View, Text, TextInput, TouchableOpacity,
  StyleSheet, ScrollView, ActivityIndicator,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '@/hooks/useAuth';
import { useCreateStudent } from '@/hooks/useCreateStudent';

interface CreatedStudent {
  email: string;
  password: string;
}

export default function NewStudentScreen() {
  const { profile } = useAuth();
  const { createStudent, loading } = useCreateStudent(profile?.id ?? '');
  const router = useRouter();

  // `router.back()` falha silenciosamente quando a navigation stack esta
  // vazia (deep link direto, refresh do browser em Expo Web, etc).
  // Fallback: replace pra lista de alunos.
  function goBack() {
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace('/(personal)/(tabs)/students' as any);
    }
  }
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState<CreatedStudent | null>(null);

  async function handleCreate() {
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) { setError('Informe o email do aluno'); return; }
    if (password.length < 6) { setError('A senha deve ter no mínimo 6 caracteres'); return; }
    setError('');
    try {
      await createStudent(trimmedEmail, password);
      setCreated({ email: trimmedEmail, password });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro ao cadastrar aluno');
    }
  }

  function handleReset() {
    setEmail('');
    setPassword('');
    setShowPassword(false);
    setError('');
    setCreated(null);
  }

  if (created) {
    return (
      <View style={styles.screen}>
        <View style={styles.header}>
          <TouchableOpacity onPress={() => goBack()}>
            <Text style={styles.backBtn}>← Voltar</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Novo Aluno</Text>
          <View style={styles.headerSpacer} />
        </View>
        <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.successCard}>
          <Text style={styles.successIcon}>✅</Text>
          <Text style={styles.successTitle}>Aluno cadastrado!</Text>
          <Text style={styles.successSubtitle}>
            Compartilhe estas informações com o aluno
          </Text>

          <View style={styles.credentialsBox}>
            <Text style={styles.credentialLabel}>Email</Text>
            <Text style={styles.credentialValue} selectable>{created.email}</Text>

            <View style={styles.divider} />

            <Text style={styles.credentialLabel}>Senha</Text>
            <View style={styles.passwordRow}>
              <Text style={styles.credentialValue} selectable>
                {showPassword ? created.password : '••••••••'}
              </Text>
              <TouchableOpacity onPress={() => setShowPassword(v => !v)}>
                <Ionicons
                  name={showPassword ? 'eye-off-outline' : 'eye-outline'}
                  size={18}
                  color="#888"
                />
              </TouchableOpacity>
            </View>
          </View>

          <Text style={styles.hint}>
            O aluno deve usar estas credenciais para entrar no app e completar o questionário inicial.
            O acesso completo é liberado por você após revisar o perfil.
          </Text>

          <TouchableOpacity style={styles.secondaryBtn} onPress={handleReset}>
            <Text style={styles.secondaryBtnText}>Cadastrar outro aluno</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.backLink} onPress={() => goBack()}>
            <Text style={styles.backLinkText}>Voltar para lista de alunos</Text>
          </TouchableOpacity>
        </View>
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => goBack()}>
          <Text style={styles.backBtn}>← Voltar</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Novo Aluno</Text>
        <View style={styles.headerSpacer} />
      </View>
      <ScrollView
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
      >
      <Text style={styles.label}>Email do aluno *</Text>
      <TextInput
        style={styles.input}
        value={email}
        onChangeText={setEmail}
        placeholder="email@exemplo.com"
        keyboardType="email-address"
        autoCapitalize="none"
        autoCorrect={false}
      />

      <Text style={[styles.label, { marginTop: 20 }]}>Senha *</Text>
      <View style={styles.passwordInputRow}>
        <TextInput
          style={[styles.input, styles.inputPassword]}
          value={password}
          onChangeText={setPassword}
          placeholder="Mínimo 6 caracteres"
          secureTextEntry={!showPassword}
          autoCapitalize="none"
          autoCorrect={false}
        />
        <TouchableOpacity style={styles.eyeBtn} onPress={() => setShowPassword(v => !v)}>
          <Ionicons
            name={showPassword ? 'eye-off-outline' : 'eye-outline'}
            size={20}
            color="#888"
          />
        </TouchableOpacity>
      </View>

      {error ? <Text style={styles.error}>{error}</Text> : null}

      <TouchableOpacity
        style={[styles.button, loading && styles.buttonDisabled]}
        onPress={handleCreate}
        disabled={loading}
      >
        {loading
          ? <ActivityIndicator color="#fff" />
          : <Text style={styles.buttonText}>Cadastrar Aluno</Text>
        }
      </TouchableOpacity>

      <Text style={styles.bottomHint}>
        Após o cadastro, o aluno entra no app com estas credenciais e completa o questionário inicial.
        O acesso completo é liberado por você após revisar o perfil.
      </Text>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: '#F5F5F5' },
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 16, paddingVertical: 14,
    backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#eee',
  },
  backBtn: { color: '#C8102E', fontSize: 15, fontWeight: '600' },
  headerTitle: { fontSize: 17, fontWeight: '700', color: '#1A1A1A' },
  headerSpacer: { width: 60 },
  content: { padding: 24 },
  label: { fontSize: 13, fontWeight: '600', color: '#555', marginBottom: 8 },
  input: {
    backgroundColor: '#fff', borderWidth: 1, borderColor: '#ddd',
    borderRadius: 10, padding: 14, fontSize: 15, color: '#1A1A1A',
    marginBottom: 4,
  },
  passwordInputRow: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
  },
  inputPassword: { flex: 1, marginBottom: 0 },
  eyeBtn: { padding: 12 },
  error: { color: '#C8102E', fontSize: 13, marginTop: 8 },
  button: {
    backgroundColor: '#C8102E', padding: 16, borderRadius: 10,
    alignItems: 'center', marginTop: 24,
  },
  buttonDisabled: { opacity: 0.5 },
  buttonText: { color: '#fff', fontWeight: 'bold', fontSize: 16 },
  bottomHint: {
    fontSize: 12, color: '#888', textAlign: 'center', marginTop: 16, lineHeight: 18,
  },
  successCard: { alignItems: 'center', paddingVertical: 16 },
  successIcon: { fontSize: 48, marginBottom: 12 },
  successTitle: { fontSize: 22, fontWeight: '700', color: '#1A1A1A', marginBottom: 4 },
  successSubtitle: {
    fontSize: 14, color: '#555', textAlign: 'center', marginBottom: 20,
  },
  credentialsBox: {
    backgroundColor: '#fff', borderWidth: 1, borderColor: '#ddd',
    borderRadius: 12, padding: 16, width: '100%', marginBottom: 16,
  },
  credentialLabel: { fontSize: 11, fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 },
  credentialValue: { fontSize: 15, color: '#1A1A1A', fontWeight: '600' },
  divider: { height: 1, backgroundColor: '#f0f0f0', marginVertical: 12 },
  passwordRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  hint: {
    fontSize: 12, color: '#888', textAlign: 'center', lineHeight: 18, marginBottom: 20,
  },
  secondaryBtn: {
    backgroundColor: '#fff', borderWidth: 1.5, borderColor: '#C8102E',
    borderRadius: 10, padding: 14, alignItems: 'center', width: '100%', marginBottom: 12,
  },
  secondaryBtnText: { color: '#C8102E', fontWeight: '700', fontSize: 15 },
  backLink: { padding: 8 },
  backLinkText: { color: '#888', fontSize: 13 },
});
